/**
 * Custom hook for running requestAnimationFrame loops with visibility handling.
 *
 * Features:
 * - Pauses when tab is hidden (Page Visibility API)
 * - Resumes when tab becomes visible
 * - Proper cleanup on unmount
 */

import { useEffect, useRef, useState } from 'react';

/**
 * Run a callback on every animation frame.
 *
 * @param callback - Function to call on each frame, receives timestamp
 * @param enabled - Whether the animation loop should run
 */
export function useAnimationLoop(
  callback: (timestamp: number) => void,
  enabled: boolean = true
): void {
  const frameRef = useRef<number | null>(null);
  const callbackRef = useRef(callback);
  const [isVisible, setIsVisible] = useState(!document.hidden);

  // Keep callback ref current without causing effect re-runs
  useEffect(() => {
    callbackRef.current = callback;
  });

  // Track page visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Animation loop
  useEffect(() => {
    if (!enabled || !isVisible) {
      return;
    }

    // Call immediately on (re)start so data appears without waiting for the
    // next frame — critical after background→foreground tab transitions.
    callbackRef.current(performance.now());

    const animate = (timestamp: number) => {
      callbackRef.current(timestamp);
      frameRef.current = requestAnimationFrame(animate);
    };

    frameRef.current = requestAnimationFrame(animate);

    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [enabled, isVisible]);
}
